/**
 * AutonomyPage — J6 autonomy facade: what the agent may do per lever, and one
 * big red handle to halt it.
 *
 * Route: /project/:projectId/autonomy
 * Data:  GET/PUT /api/projects/:projectId/autonomy/v1/settings
 *        GET     /api/projects/:projectId/autonomy/v1/gates (independent)
 *
 * Composition per the user-confirmed mocks (.design/autonomy-facade/mockups/):
 * header + G9/lift chips → kill-switch panel at the TOP even when disengaged
 * (confirmed decision) → read-error banner (AC-EH3) → actionError + levers
 * Paper (5 LeverRows) → enablement checklist → activation-gates board with
 * its OWN independent error state (a gates failure renders check-unavailable
 * rows with Retry; the page never blanks and levers stay usable — AC-EH2) →
 * recent wGate refusals.
 *
 * Settings load failure → honest full-page error with Retry. Every save is
 * imperative PUT + refetch (busy lock, actionError separate — AC-EH1); the
 * gates board refetches after each save so chips/G4/G10 stay server-truthful.
 */

import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { AUTONOMY_TEST_IDS } from '../testIds';
import { AutonomyApiError } from '../api/autonomyClient';
import { useAutonomySettings } from '../hooks/useAutonomySettings';
import { useAutonomyGates } from '../hooks/useAutonomyGates';
import type { AutonomyLever, AutonomyTier } from '../types/autonomy.types';
import { orderGates } from '../utils/gates';
import { LeverRow } from '../components/LeverRow';
import { KillSwitchPanel } from '../components/KillSwitchPanel';
import { EnablementChecklist } from '../components/EnablementChecklist';
import { GateRow } from '../components/GateRow';
import { RefusalPanel } from '../components/RefusalPanel';
import { TrackRecordChips } from '../components/TrackRecordChips';

const CONTENT_MAX_WIDTH = 980;

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="subtitle1"
      sx={{ fontWeight: 500, px: 2.5, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}
    >
      {children}
    </Typography>
  );
}

function RowsSkeleton({ rows }: { rows: number }) {
  return (
    <>
      {Array.from({ length: rows }, (_, index) => (
        <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 1.5 }}>
          <Skeleton variant="circular" width={22} height={22} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="40%" />
            <Skeleton variant="text" width="70%" />
          </Box>
        </Box>
      ))}
    </>
  );
}

/** Save-action error copy: the backend 400/422 reason verbatim when present. */
function actionErrorText(error: Error): string {
  if (error instanceof AutonomyApiError && error.reason) return error.reason;
  return error.message;
}

export const AutonomyPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const settings = useAutonomySettings(projectId);
  const gates = useAutonomyGates(projectId);

  // AC-EH3 read-error resolution: the settings GET succeeds fail-closed (all
  // levers L1) while the gates response marks killSwitch unknown — the only
  // server-computed signal that the config was unreadable.
  const readError = gates.data?.killSwitch.unknown === true;

  const handleNavigate = (deepLink: string) => navigate(deepLink);
  const handleRetrySettings = () => { void settings.refetch(); };
  const handleRetryGates = () => { void gates.refetch(); };
  const handleRetryAll = () => { void settings.refetch(); void gates.refetch(); };

  // Saves refetch settings internally (AC-EH1); the gates board is refetched
  // after so G4/G10/refusals/chips reflect the new server state.
  const handleTierChange = (lever: AutonomyLever, tier: AutonomyTier) => {
    void settings.setLeverTier(lever, tier).then(() => gates.refetch());
  };
  const handleEngage = () => {
    void settings.setKillSwitch(true).then(() => gates.refetch());
  };
  const handleDisengage = () => {
    void settings.setKillSwitch(false).then(() => gates.refetch());
  };

  return (
    <Box data-testid={AUTONOMY_TEST_IDS.page} sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ maxWidth: CONTENT_MAX_WIDTH }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Autonomy
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          What the agent may do on this project, per lever — the tier shown here is the tier the
          write gate enforces.
        </Typography>
      </Box>

      {/* Honest full-page error state for the settings load (no white page) */}
      {settings.error && !settings.data && !settings.loading && (
        <Alert
          severity="error"
          sx={{ maxWidth: CONTENT_MAX_WIDTH }}
          action={(
            <Button
              data-testid={AUTONOMY_TEST_IDS.retry}
              color="inherit"
              size="small"
              onClick={handleRetrySettings}
            >
              Retry
            </Button>
          )}
        >
          <AlertTitle>Could not load autonomy settings</AlertTitle>
          {settings.error.message}
        </Alert>
      )}

      {settings.loading && !settings.error && (
        <Box sx={{ maxWidth: CONTENT_MAX_WIDTH }}>
          <Paper>
            <RowsSkeleton rows={5} />
          </Paper>
        </Box>
      )}

      {settings.data && !settings.loading && (
        <Box sx={{ maxWidth: CONTENT_MAX_WIDTH }}>
          {/* A settings refetch that failed while stale data is on screen */}
          {settings.error && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Refresh failed: {settings.error.message}
            </Alert>
          )}

          {/* G9 track-record + G6 lift chips (server-computed modes) */}
          {gates.data && (
            <TrackRecordChips trackRecord={gates.data.trackRecord} lift={gates.data.lift} />
          )}

          {/* Kill switch — always at the top under the chips (confirmed) */}
          <KillSwitchPanel
            engaged={settings.data.killSwitch}
            busy={settings.busyLever !== null}
            saving={settings.busyLever === 'killSwitch'}
            rollbackLinks={gates.data?.rollbackLinks ?? []}
            onEngage={handleEngage}
            onDisengage={handleDisengage}
          />

          {/* AC-EH3 read-error banner — everything Observe + writes refused */}
          {readError && (
            <Alert
              severity="error"
              data-testid={AUTONOMY_TEST_IDS.readErrorBanner}
              sx={{ mb: 2 }}
              action={(
                <Button
                  data-testid={AUTONOMY_TEST_IDS.retry}
                  color="inherit"
                  size="small"
                  onClick={handleRetryAll}
                >
                  Retry
                </Button>
              )}
            >
              <AlertTitle>Autonomy settings could not be read</AlertTitle>
              Every lever is shown at L1 (Observe) and agent writes are refused until the
              configuration is readable again. An unreadable config never grants.
            </Alert>
          )}

          {/* Save-action error near the levers — 400/422 reasons verbatim */}
          {settings.actionError && (
            <Alert
              severity="error"
              data-testid={AUTONOMY_TEST_IDS.actionError}
              sx={{ mb: 2 }}
            >
              {actionErrorText(settings.actionError)}
            </Alert>
          )}

          {/* Levers */}
          <Paper sx={{ mb: 2 }}>
            <SectionHeader>Levers</SectionHeader>
            {settings.data.levers.map((lever) => (
              <LeverRow
                key={lever.lever}
                lever={lever}
                busyLever={settings.busyLever}
                onTierChange={handleTierChange}
              />
            ))}
          </Paper>

          <EnablementChecklist items={settings.data.enablement} onNavigate={handleNavigate} />

          {/* Activation gates board — OWN independent error state (AC-EH2):
              a gates failure renders the check-unavailable row with Retry;
              the settings half above stays fully usable. */}
          <Paper sx={{ mb: 2 }}>
            <SectionHeader>Activation gates G1–G10</SectionHeader>
            {gates.loading && !gates.data && <RowsSkeleton rows={4} />}
            {gates.error && !gates.data && !gates.loading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 1.5 }}>
                <Box
                  component="span"
                  aria-label="Check unavailable"
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    border: '2px dotted',
                    borderColor: 'divider',
                    color: 'text.secondary',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.7rem',
                    lineHeight: 1,
                    flex: 'none',
                  }}
                >
                  …
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body1">Gates check unavailable</Typography>
                  <Typography variant="body2" color="text.secondary">
                    The gates endpoint did not respond. Lever settings above are unaffected and
                    stay editable.
                  </Typography>
                </Box>
                <Button
                  data-testid={AUTONOMY_TEST_IDS.gatesRetry}
                  variant="outlined"
                  size="small"
                  sx={{ whiteSpace: 'nowrap', flex: 'none' }}
                  onClick={handleRetryGates}
                >
                  Retry
                </Button>
              </Box>
            )}
            {gates.data && orderGates(gates.data.gates).map((gate) => (
              <GateRow key={gate.gateId} gate={gate} />
            ))}
          </Paper>

          {/* Recent wGate refusals (from trajectories, server-projected) */}
          {gates.data && <RefusalPanel refusals={gates.data.recentRefusals} />}
        </Box>
      )}
    </Box>
  );
};

export default AutonomyPage;
